import type { ResponseProjectBlockDto } from '../api/magellan/responseProjectBlock/dto';
import type { RAGStatus } from './RAGStatus';

export type FindAllResponseProjectBlocksResponseDTO = {
  blockGenerationStatus: RAGStatus;
  blocks: ResponseProjectBlockDto[];
};

export interface ResponseProjectBlock {
  id: number;
  projectId: number;
  title: string;
  description?: string;
  content?: string;
  index: number;
  depth: number;
  getChildren(): ResponseProjectBlock[];
  hasChildren(): boolean;
  isCompleted(): boolean;
  getBlockSuffix(): string;
  getIdOfParentsAndSelf(): number[];
  getIdOfChildrenAndSelf(options?: {
    includeOnlyBlockWithChildren?: boolean;
  }): number[];
  getLastChild(): ResponseProjectBlock | null;
  threadId?: number;
  updateThreadId(threadId: number): void;
}

interface ResponseProjectBlockOptions {
  parentBlock: ResponseProjectBlock | null;
  depth: number;
}

export class ResponseProjectBlockModel implements ResponseProjectBlock {
  private parentBlock: ResponseProjectBlock | null;
  private childBlocks: ResponseProjectBlock[];

  id: number;
  projectId: number;
  title: string;
  description?: string;
  content?: string;
  index: number;
  depth: number;

  threadId?: number;

  options: ResponseProjectBlockOptions;

  constructor(
    block: ResponseProjectBlockDto,
    options: ResponseProjectBlockOptions | undefined = {
      parentBlock: null,
      depth: 1,
    },
  ) {
    this.id = block.id;
    this.projectId = block.projectId;
    this.title = block.title;
    this.description = block.description;
    this.content = block.content;
    this.index = block.index;
    this.options = options;
    this.parentBlock = options.parentBlock;
    this.depth = this.options.depth;
    this.childBlocks = this.instantiateChildBlocks(block);
    this.threadId = block.threadId;
  }

  hasChildren(): boolean {
    return this.childBlocks.length > 0;
  }

  getChildren(): ResponseProjectBlock[] {
    return this.childBlocks;
  }

  getLastChild(): ResponseProjectBlock | null {
    return this.childBlocks[this.childBlocks.length - 1];
  }

  isCompleted(): boolean {
    return !!this.content;
  }

  getBlockSuffix(): string {
    const parents = this.getParent();
    if (parents) {
      return parents.getBlockSuffix() + '.' + this.indexToBlockSuffix();
    }
    return this.indexToBlockSuffix();
  }

  getIdOfParentsAndSelf(): number[] {
    const parents = this.getParent();
    if (parents) {
      return [...parents.getIdOfParentsAndSelf(), this.id];
    }
    return [this.id];
  }

  getIdOfChildrenAndSelf(options: {
    includeOnlyBlockWithChildren?: boolean;
  }): number[] {
    const childIds = this.childBlocks.flatMap(child =>
      child.getIdOfChildrenAndSelf(options),
    );

    if (options.includeOnlyBlockWithChildren && !this.hasChildren()) {
      return [];
    }

    return [...childIds, this.id];
  }

  updateThreadId(threadId: number): void {
    this.threadId = threadId;
  }

  private indexToBlockSuffix(): string {
    return this.index.toString();
  }

  private instantiateChildBlocks(
    block: ResponseProjectBlockDto,
  ): ResponseProjectBlock[] {
    return block.childBlocks.map(
      child =>
        new ResponseProjectBlockModel(child, {
          parentBlock: this,
          depth: this.options.depth + 1,
        }),
    );
  }

  private getParent(): ResponseProjectBlock | null {
    return this.parentBlock;
  }
}
